import React, { useState } from 'react';
import type { TripsMonitoredUsageData } from '../../types/tripsMonitoredUsage';
import { ALL_ALERTS, ALERT_CHANNELS, type AlertFilterType } from '../../types/alertFilter';
import type { Granularity } from '../../types/timeframe';
import MetricScorecardBar from './components/MetricScorecardBar';
import UsageBarChart from './components/UsageBarChart';
import TripGroupsTable from './components/TripGroupsTable';
import AlertsByTypeTable from './components/AlertsByTypeTable';
import { exportToCsv } from './utils/exportCsv';
import SkeletonLoader from './components/SkeletonLoader';
import styles from './tripsMonitoredTab.module.css';

interface TripsMonitoredTabProps {
  data: TripsMonitoredUsageData | null;
  loading: boolean;
  error: boolean;
  showAlertsSent: boolean;
  showEmailsSent: boolean;
  showWaiverMetric: boolean;
  timeframeSubtitle: string;
  dateRangeLabel: string;
  granularity: Granularity;
}

const TripsMonitoredTab: React.FC<TripsMonitoredTabProps> = ({
  data,
  loading,
  error,
  showAlertsSent,
  showEmailsSent,
  showWaiverMetric,
  timeframeSubtitle,
  dateRangeLabel,
  granularity,
}) => {
  const [alertFilterType, setAlertFilterType] = useState<AlertFilterType>(ALL_ALERTS);

  if (loading) {
    return <SkeletonLoader />;
  }

  if (error) {
    return (
      <div className={styles.stateMessage}>
        <span className={styles.stateMessageTitle}>Something went wrong</span>
        <span className={styles.stateMessageSubtitle}>Please try again.</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.stateMessage}>
        <span className={styles.stateMessageTitle}>No data available for the selected range</span>
        <span className={styles.stateMessageSubtitle}>Try adjusting the date range or filters to see results.</span>
      </div>
    );
  }

  const metrics = [
    { label: 'Total Trips Monitored', value: data.metrics.totalTripsMonitored ?? 0, subtitle: dateRangeLabel },
    { label: 'Total Alerts Sent (API)', value: data.metrics.totalAlertsSent ?? 0, subtitle: dateRangeLabel },
    { label: 'Total Alerts Sent (Email)', value: data.metrics.totalEmailsSent ?? 0, subtitle: dateRangeLabel },
  ];

  // Prefer the chronologically-sorted union computed from raw period values;
  // fall back (e.g. mock data) to the union of keys across all rows so we
  // don't drop periods that only appear on later rows.
  const periodColumns =
    data.tripGroupPeriodColumns ??
    Array.from(
      data.tripGroupsTable.reduce((cols, row) => {
        Object.keys(row).forEach(key => {
          if (key !== 'tripGroup') cols.add(key);
        });
        return cols;
      }, new Set<string>())
    );

  // Only offer channels that actually have alert data in the response.
  const availableChannels = ALERT_CHANNELS.filter(
    channel => data.alertsByChannel[channel].length > 0
  );

  // When only one channel has data, select it directly instead of showing "All Alerts".
  const effectiveFilterType = availableChannels.length === 1 ? availableChannels[0] : alertFilterType;
  const alertRows = data.alertsByChannel[effectiveFilterType] || [];

  // Prefer the chronologically-sorted columns computed from raw period values;
  // fall back (e.g. mock data) to the union of keys across rows for the
  // selected channel — different alert types can have data on different
  // periods, so relying on a single row would drop periods only present elsewhere.
  const alertPeriodColumns =
    data.alertPeriodColumns ??
    Array.from(
      alertRows.reduce((cols, row) => {
        Object.keys(row).forEach(key => {
          if (key !== 'alertType') cols.add(key);
        });
        return cols;
      }, new Set<string>())
    );

  const handleExportTripGroups = () => {
    exportToCsv(
      data.tripGroupsTable as Record<string, any>[],
      'trips-by-trip-group.csv',
      ['tripGroup', ...periodColumns],
    );
  };

  const handleExportAlerts = () => {
    exportToCsv(
      alertRows as Record<string, any>[],
      'alerts-by-type.csv',
      ['alertType', ...alertPeriodColumns],
    );
  };

  return (
    <div className={styles.tab}>
      <MetricScorecardBar metrics={metrics} />
      <UsageBarChart
        title={"Trips monitored: " + timeframeSubtitle}
        chartData={data.chartData}
        dataLabel="Trips Monitored"
        aggregator={granularity}
      />
      <div className={styles.tablesRow}>
        <div className={styles.tableHalf}>
          <TripGroupsTable
            data={data.tripGroupsTable}
            periodColumns={periodColumns}
            onExport={handleExportTripGroups}
          />
        </div>
        <div className={styles.tableHalf}>
          <AlertsByTypeTable
            data={alertRows}
            periodColumns={alertPeriodColumns}
            filterType={effectiveFilterType}
            onFilterChange={setAlertFilterType}
            availableChannels={availableChannels}
            onExport={handleExportAlerts}
          />
        </div>
      </div>
    </div>
  );
};

export default TripsMonitoredTab;
